/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { Loader, Button, Icon, Input, Select } from "semantic-ui-react";
import { Notification } from "../../../api";
import { useAuth, useNotifications } from "../../../hooks";
import { BasicModal } from "../../../components/Shared";
import {
  ListNotifications,
  NotificationForm,
} from "../../../components/Admin/Notifications";
import "./Notifications.scss";

const notificationController = new Notification();

export function Notifications() {
  const { user, accessToken } = useAuth();
  const { reloadNotifications } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [reload, setReload] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);

  const onReload = () => setReload((prevState) => !prevState);
  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);

  const handleItemsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (e, { value }) => {
    setTypeFilter(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e, { value }) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const getStatusColor = (viewed) => {
    return viewed ? "blue" : "grey";
  };

  const getStatusText = (viewed) => {
    return viewed ? "LEÍDA" : "NO LEÍDA";
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "grade":
        return "orange";
      case "payment":
        return "teal";
      case "forum":
        return "purple";
      case "course":
        return "blue";
      case "event":
        return "olive";
      case "general":
        return "grey";
      default:
        return "brown";
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case "grade":
        return "CALIFICACIÓN";
      case "payment":
        return "PAGO";
      case "forum":
        return "FORO";
      case "course":
        return "CURSO";
      case "event":
        return "EVENTO";
      case "general":
        return "GENERAL";
      default:
        return "SISTEMA";
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationController.updateNotification(notificationId, {
        viewed: true,
      });
      onReload();
      reloadNotifications();
    } catch (error) {
      // Error handling
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationController.readAllNotifications(user.id);
      onReload();
      reloadNotifications();
    } catch (error) {
      // Error handling
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await notificationController.deleteNotification(notificationId);
      onReload();
      reloadNotifications();
    } catch (error) {
      // Error handling
    }
  };

  const tableConfig = {
    headers: ["Fecha", "Tipo", "Título", "Mensaje", "Estado", "Acciones"],
    fields: [
      {
        field: "createdAt",
        dataType: "date",
        format: "DD/MM/YYYY hh:mm a",
        align: "center",
      },
      {
        field: "type",
        component: "Label",
        getColor: getTypeColor,
        getText: getTypeText,
        align: "center",
      },
      {
        field: "title",
        dataType: "text",
        align: "left",
      },
      {
        field: "message",
        dataType: "text",
        align: "left",
      },
      {
        field: "viewed",
        component: "Label",
        getColor: getStatusColor,
        getText: getStatusText,
        align: "center",
      },
      {
        buttons: [
          {
            color: "green",
            icon: "check",
            placeholder: "Marcar como leída",
            onClick: (row) => markAsRead(row.id),
            disabled: "row.viewed === true",
          },
          {
            color: "red",
            icon: "trash",
            placeholder: "Eliminar notificación",
            onClick: (row) => deleteNotification(row.id),
          },
        ],
      },
    ],
    columnWidths: ["15%", "12%", "20%", "30%", "13%", "10%"],
  };

  useEffect(() => {
    (async () => {
      setLoadingNotifications(true);
      try {
        const filters = {
          type: typeFilter,
          viewed: statusFilter,
          search: search,
        };

        const response = await notificationController.getNotificationsPaginated(
          currentPage,
          limit,
          filters
        );
        setNotifications(response);
      } catch (error) {
        // Error handling
      } finally {
        setLoadingNotifications(false);
      }
    })();
  }, [
    currentPage,
    limit,
    reload,
    accessToken,
    typeFilter,
    statusFilter,
    search,
  ]);

  return (
    <div className="admin-notifications">
      <div className="admin-notifications__header">
        <Button
          className="admin-notifications__add"
          primary
          onClick={onOpenCloseModal}
        >
          Nueva notificación
        </Button>
      </div>
      <div className="admin-notifications__filters">
        <Select
          className="edunova-select"
          placeholder="Selecciona un tipo"
          options={[
            { key: "", value: "", text: "Selecciona un tipo" },
            { key: "grade", value: "grade", text: "Calificación" },
            { key: "payment", value: "payment", text: "Pago" },
            { key: "forum", value: "forum", text: "Foro" },
            { key: "course", value: "course", text: "Curso" },
            { key: "event", value: "event", text: "Evento" },
            { key: "general", value: "general", text: "General" },
          ]}
          value={typeFilter}
          onChange={handleTypeFilterChange}
          clearable
        />
        <Select
          className="edunova-select"
          placeholder="Selecciona un estado"
          options={[
            { key: "", value: "", text: "Selecciona un estado" },
            { key: "unread", value: false, text: "No leídas" },
            { key: "read", value: true, text: "Leídas" },
          ]}
          value={statusFilter}
          onChange={handleStatusFilterChange}
          clearable
        />
        <Input
          icon="search"
          iconPosition="left"
          type="text"
          placeholder="Buscar por título o mensaje..."
          value={search}
          onChange={handleSearchChange}
        />
        <Button
          color="grey"
          onClick={() => {
            setSearch("");
            setTypeFilter("");
            setStatusFilter("");
            setCurrentPage(1);
          }}
        >
          <Icon name="eraser" />
          Limpiar filtros
        </Button>
        <Button
          color="green"
          onClick={markAllAsRead}
          disabled={!notifications.data || notifications.data.length === 0}
        >
          <Icon name="check circle" />
          Marcar todas como leídas
        </Button>
      </div>
      <ListNotifications
        notifications={notifications?.data}
        loading={loadingNotifications || !notifications}
        error={null}
        pagination={notifications?.pagination}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        tableConfig={tableConfig}
        onReload={onReload}
      />

      <BasicModal
        show={showModal}
        close={onOpenCloseModal}
        title="Nueva notificación"
        size="small"
      >
        <NotificationForm onClose={onOpenCloseModal} onReload={onReload} />
      </BasicModal>
    </div>
  );
}
